/** Tall two-wheel steel bin for the service corridor. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

function taperedBinBody(): THREE.BufferGeometry {
  // The narrow lower shell is structural rather than decorative: a parallel-sided
  // body read as an appliance in the first blocking pass, even with a lid and wheels.
  const positions = new Float32Array([
    -0.27, 0.09, -0.22, 0.27, 0.09, -0.22, 0.27, 0.09, 0.22, -0.27, 0.09, 0.22,
    -0.34, 1.075, -0.27, 0.34, 1.075, -0.27, 0.34, 1.075, 0.27, -0.34, 1.075, 0.27,
  ]);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex([
    0, 1, 5, 0, 5, 4, 1, 2, 6, 1, 6, 5,
    2, 3, 7, 2, 7, 6, 3, 0, 4, 3, 4, 7,
    0, 3, 2, 0, 2, 1, 4, 5, 6, 4, 6, 7,
  ]);
  geometry.computeVertexNormals();
  return geometry;
}

function mesh(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const part = new THREE.Mesh(geometry, material);
  part.name = name;
  part.castShadow = true;
  part.receiveShadow = true;
  return part;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'wheeled-bin';

  const steel = surface('metal', 0x7d8585);
  const darkSteel = surface('metal', 0x50595a);
  // Organic is the existing non-fabric dark, low-glint surface kind; the shader
  // intentionally has no separate rubber kind, so introducing one here would
  // turn a single kit asset into an unreviewed rendering-system change.
  const rubber = surface('organic', 0x252a2a);

  group.add(mesh(taperedBinBody(), steel, 'tapered-steel-body'));

  const rim = new THREE.Group();
  rim.name = 'body-rim';
  const rimParts: readonly [readonly [number, number, number], readonly [number, number, number], string][] = [
    [[0.70, 0.035, 0.025], [0, 1.0675, -0.2675], 'rim-back'],
    [[0.70, 0.035, 0.025], [0, 1.0675, 0.2675], 'rim-front'],
    [[0.025, 0.035, 0.54], [-0.3375, 1.0675, 0], 'rim-left'],
    [[0.025, 0.035, 0.54], [0.3375, 1.0675, 0], 'rim-right'],
  ];
  for (const [size, position, name] of rimParts) {
    const bar = mesh(new THREE.BoxGeometry(...size), darkSteel, name);
    bar.position.set(...position);
    rim.add(bar);
  }
  group.add(rim);

  // This group is the hinge-line pivot, not merely a named lid mesh. A later
  // interaction can rotate it around local X without making the front lip orbit
  // around the bin's centre, which is how a supposedly hinged lid becomes a flap.
  const lid = new THREE.Group();
  lid.name = 'lid';
  lid.position.set(0, 1.085, -0.275);
  const lidPanel = mesh(new THREE.BoxGeometry(0.72, 0.05, 0.56), steel, 'closed-lid-panel');
  lidPanel.position.set(0, 0.04, 0.285);
  const frontLip = mesh(new THREE.BoxGeometry(0.72, 0.07, 0.025), darkSteel, 'lid-front-lip');
  frontLip.position.set(0, 0.03, 0.5725);
  const hinge = mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.68, 8), darkSteel, 'lid-hinge-bar');
  hinge.rotation.z = Math.PI / 2;
  hinge.position.set(0, 0.025, 0.018);
  lid.add(lidPanel, frontLip, hinge);
  group.add(lid);

  const runningGear = new THREE.Group();
  runningGear.name = 'two-wheel-running-gear';
  const axle = mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.61, 8), darkSteel, 'rear-axle');
  axle.rotation.z = Math.PI / 2;
  axle.position.set(0, 0.105, -0.195);
  runningGear.add(axle);
  for (const [x, name] of [[-0.31, 'rear-wheel-left'], [0.31, 'rear-wheel-right']] as const) {
    const wheel = mesh(new THREE.CylinderGeometry(0.09, 0.09, 0.045, 10), rubber, name);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(x, 0.10, -0.195);
    runningGear.add(wheel);
  }
  group.add(runningGear);

  const handles = new THREE.Group();
  handles.name = 'rear-handle-bars';
  for (const [x, name] of [[-0.235, 'handle-left'], [0.235, 'handle-right']] as const) {
    const upright = mesh(new THREE.BoxGeometry(0.035, 0.30, 0.035), darkSteel, name);
    upright.position.set(x, 0.77, -0.2925);
    handles.add(upright);
  }
  const crossbar = mesh(new THREE.BoxGeometry(0.505, 0.035, 0.035), darkSteel, 'handle-crossbar');
  crossbar.position.set(0, 0.91, -0.2925);
  handles.add(crossbar);
  group.add(handles);

  const frontRest = mesh(new THREE.BoxGeometry(0.42, 0.05, 0.08), darkSteel, 'forward-ground-rest');
  frontRest.position.set(0, 0.025, 0.225);
  group.add(frontRest);

  return group;
}

export const wheeledBinAsset: AssetDefinition = {
  id: 'wheeled-bin',
  label: 'Wheeled steel bin',
  description: 'Tall tapered two-wheel steel bin with a closed hinged lid.',
  footprint: { width: 0.72, height: 1.15, length: 0.62 },
  create: build,
};
